import { useEffect } from 'react'
import type { JSX } from 'react'
import { format } from 'date-fns'

import { Button, Image, UpgradeDialog } from '@buffer-mono/popcorn'

import { useUser } from '../../../../common/context/User'
import getCopy from './getCopy'
import trackTrialWelcomeModalViewed from '../../../../tracking/TrialPaths/trackTrialWelcomeModalViewed'
import { getSubscriptionPeriodEnd } from '../../../../common/utils/billing'
import { MODALS } from '../../../../common/types'
import { shouldShowChannelConnectionPrompt } from '../../utils'
import { ConfirmationFeaturesList } from './components/ConfirmationFeaturesList'
import {
  getOrganizationId,
  getTrialLengthInDays,
  getUserBillingData,
} from '../../../../common/utils/user'

import { useModalManager } from '../../../ModalManager/hooks/useModalManager'

const Confirmation = (): JSX.Element | null => {
  const user = useUser()
  const { modalData, openModal, dismissModal: closeAction } = useModalManager()

  const startedTrial = modalData?.startedTrial

  useEffect(() => {
    const upgradePathName = 'trialWelcomeModal'
    const payload = {
      ctaButton: 'trialStarted',
      upgradePathName,
      ctaView: 'trialWelcomeModal',
      ctaLocation: 'trialWelcomeModal',
    }

    if (startedTrial) {
      trackTrialWelcomeModalViewed({
        payload,
        user,
      })
    }
  }, [user, startedTrial])

  if (!modalData || (!modalData.selectedPlan && !startedTrial)) {
    return null
  }

  const {
    selectedPlan,
    stayedOnSamePlan,
    isMigrationFromMP,
    isDowngradingToFreePlan,
    scheduledUpdate,
    currentPlanId,
    currentPlanChannelQuantity,
  } = modalData

  const planName = selectedPlan?.planName || ''
  const billingData = getUserBillingData(user)
  const subscriptionEndDate =
    billingData && getSubscriptionPeriodEnd(billingData)
  const planEndDate = subscriptionEndDate
    ? format(new Date(subscriptionEndDate), 'dd/MM/yyyy')
    : format(new Date(), 'dd/MM/yyyy') // Todays date
  const organizationId = getOrganizationId(user)

  const { label, description, buttonCopy, imageUrl, footer, featuresList } =
    getCopy({
      planName,
      startedTrial,
      trialLengthInDays: getTrialLengthInDays(user),
      stayedOnSamePlan,
      isMigrationFromMP,
      isDowngradingToFreePlan,
      planEndDate,
      organizationId,
      scheduledUpdate,
      currentPlanId,
      currentPlanChannelQuantity,
    })

  const onCloseHandle = (): void => {
    /* when the modal is closed, we check if we should open the channelconnection prompt */
    closeAction()

    if (shouldShowChannelConnectionPrompt(user)) {
      openModal({ key: MODALS.channelConnectionPrompt })
    }
  }

  return (
    <UpgradeDialog open={true} onOpenChange={onCloseHandle}>
      <UpgradeDialog.Content>
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>{label}</UpgradeDialog.Title>
          <UpgradeDialog.Description>{description}</UpgradeDialog.Description>
          {startedTrial ? (
            <ConfirmationFeaturesList featuresList={featuresList} />
          ) : (
            <Button
              id="confirmation"
              variant="primary"
              data-testid="confirmation-button"
              aria-label={buttonCopy}
              onClick={onCloseHandle}
            >
              {buttonCopy}
            </Button>
          )}
          {footer && footer}
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image alt="Start Trial" src={imageUrl} />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}

export default Confirmation
